import React, { useEffect, useMemo, useState } from 'react';
import { HiArrowsUpDown } from 'react-icons/hi2';
import { toast } from 'react-hot-toast';
import SessionConfigPicker from '../../../components/SessionConfigPicker';
import { configOptionLabel, SessionConfigChoice } from '../../../utils/sessionConfigChoice';
import { EditOperationMode, isOperationLocked } from '../../SessionView/SessionDetails/SessionConfigs/editOperationUtils';
import { useRunConfigMove } from './useRunConfigMove';

interface ConfigSelectorProps {
  run: any;
  config: any;
  session: any;
  setShowEditMenu: any;
  configs?: any[];
  sessions?: any[];
  sessionsLoading?: boolean;
  invalidateRuns?: any;
  configMovesToSave?: { [key: string]: () => Promise<void> };
  editOpMode?: EditOperationMode;
  setEditOpMode?: (mode: EditOperationMode) => void;
}

const ConfigSelector: React.FC<ConfigSelectorProps> = ({
  run,
  config,
  session,
  setShowEditMenu,
  configs,
  sessions,
  sessionsLoading,
  invalidateRuns,
  configMovesToSave,
  editOpMode,
  setEditOpMode,
}) => {
  const { moveRunToConfig } = useRunConfigMove({
    run,
    config,
    configs,
    sessions,
    invalidateRuns,
    configMovesToSave,
    editOpMode,
    setEditOpMode,
  });

  const [selectedConfig, setSelectedConfig] = useState(config);
  // The user's explicit "Move to Session" pick, or null while the row is still
  // showing its own session. Kept apart from the resolved session below so a
  // runs refetch can refresh that one without discarding this one.
  const [pickedSession, setPickedSession] = useState<any>(null);

  // Re-sync only when the run genuinely lands on a different config/session.
  // Keying on the objects themselves resets the user's in-progress pick on every
  // runs refetch, because a refetch hands down structurally identical but
  // freshly-allocated props — which is exactly what leaves the Move button
  // disabled no matter how many times the target is chosen.
  const configId = config?.id;
  const sessionId = session?.id;
  useEffect(() => {
    setSelectedConfig(config);
    setPickedSession(null);
  }, [sessionId, configId]);

  // The run's own session, resolved against the sessions query so it carries
  // `configs`. Derived rather than mirrored into state: in the run list the
  // sessions query resolves AFTER first render, and an effect keyed on
  // [sessionId, configId] never re-runs to pick the full object up — leaving
  // the config dropdown empty for the rest of the page's life. The sparse
  // `session` prop remains the fallback until the query lands.
  const runSession = useMemo(
    () => (sessionId ? sessions?.find((s: any) => s.id === sessionId) : undefined) ?? session,
    [sessions, sessionId, session],
  );
  const selectedSession = pickedSession ?? runSession;

  const handleConfigChange = async (newConfigId: any) => {
    const availableConfigs = selectedSession?.configs || configs;
    const newConfig = availableConfigs?.find((c: any) => c.id === newConfigId);

    setSelectedConfig(newConfig);
    // Update the run's config_id
    if (run.id) {
      try {
        const result = await moveRunToConfig(newConfigId);
        if (result === 'locked') return;
        setShowEditMenu(false);
      } catch (error) {
        console.error(error);
        toast.error('Error updating run config');
        // Revert the selection if the API call fails
        setSelectedConfig(config);
      }
    }
  };

  // The picker hands back the whole choice. `pickedSession` only moves when the
  // session itself does: a config pick must leave it null while the row is
  // still on its own session, or it would pin that session's stale copy.
  const handleDestinationChange = (choice: SessionConfigChoice) => {
    if (choice.session?.id !== selectedSession?.id) setPickedSession(choice.session);
    setSelectedConfig(choice.config);
  };

  return (
    <>
      <SessionConfigPicker
        sessions={sessions}
        sessionsLoading={sessionsLoading}
        value={{ session: selectedSession ?? null, config: selectedConfig ?? null }}
        onChange={handleDestinationChange}
        sessionLabel="Move to Session:"
        configLabel="Move to Config:"
        action={
          // Solid primary blue: cirrusBlue-100 read as a disabled grey chip
          // even when the button was live. Disabled keeps the washed-out
          // look — that state really is inert.
          <button
            type="button"
            className="cr-rounded cr-border-none cr-bg-blue-900 cr-px-3 cr-py-1 cr-text-xs
              cr-font-semibold cr-text-white cr-transition-colors hover:cr-bg-blue-850
              disabled:cr-cursor-not-allowed disabled:cr-bg-cirrusBlue-100 disabled:cr-opacity-60"
            disabled={!selectedConfig}
            onClick={() => selectedConfig && handleConfigChange(selectedConfig.id)}
          >
            Move
          </button>
        }
      />
      {selectedConfig && configs && configs.length > 0 && (
        <div className={`cr-px-4 cr-py-2 cr-text-sm cr-text-gray-700 ${isOperationLocked(editOpMode, 'run_move') ? 'cr-opacity-40' : ''}`}>
          <div className="cr-flex cr-items-center cr-gap-2">
            <HiArrowsUpDown className="cr-size-4" />
            <span>Move to Config:</span>
          </div>
          <select
            className="cr-ml-4 cr-w-11/12 cr-rounded cr-border cr-border-gray-300
                              cr-bg-white cr-px-2 cr-py-1 cr-text-xs"
            value={selectedConfig.id}
            onChange={(e) => handleConfigChange?.(e.target.value)}
            disabled={isOperationLocked(editOpMode, 'run_move')}
            title={isOperationLocked(editOpMode, 'run_move') ? 'Save pending changes first' : undefined}
          >
            {configs?.map((c: any) => (
              <option key={c.id} value={c.id}>
                {configOptionLabel(c)}
              </option>
            ))}
          </select>
        </div>
      )}
    </>
  );
};

export default ConfigSelector;
